// frontend/src/features/portfolio-diversification-optimizer-pro/components/FactorExposurePanel.tsx
//
// Pillar 3 -- Multi-Factor OLS & Variance Decomposition. Splits portfolio
// variance into systematic macro factor risk (R^2) vs. idiosyncratic
// asset-specific risk (1 - R^2), renders side-by-side Recharts bar charts for
// factor betas and Euler variance-contribution share, and a statistical
// significance table with t-statistic confidence badges.
//
// Units note: the backend emits `systematic_variance_share`,
// `idiosyncratic_variance_share`, and each factor's `variance_contribution_pct`
// as FRACTIONS (0..1), so they are multiplied by 100 for display here. Beta,
// correlation, t-stat are raw numbers; annualized_factor_vol is a fraction.
//
// Themed for the shared light/dark dashboard shell.

import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Compass, Cpu, Layers, Sliders } from "lucide-react";

import type { FactorExposureAnalysis } from "../types";

interface FactorExposurePanelProps {
  factorExposure: FactorExposureAnalysis;
}

const CHART_GRID = "#e2e8f0";
const CHART_AXIS = "#94a3b8";
const TOOLTIP_STYLE: React.CSSProperties = {
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  fontSize: 12,
  background: "rgba(255,255,255,0.97)",
  color: "#0f172a",
};

const cardClass =
  "rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900/90";

function getSignificanceBadge(tStat: number) {
  const absT = Math.abs(tStat);
  if (absT >= 2.58) {
    return {
      label: "p < 0.01 (99% Sig)",
      classes: "bg-emerald-500/15 text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300",
    };
  }
  if (absT >= 1.96) {
    return {
      label: "p < 0.05 (95% Sig)",
      classes: "bg-sky-500/15 text-sky-700 ring-1 ring-sky-500/30 dark:text-sky-300",
    };
  }
  if (absT >= 1.65) {
    return {
      label: "p < 0.10 (Marginal)",
      classes: "bg-amber-500/15 text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-300",
    };
  }
  return {
    label: "Insignificant",
    classes: "bg-slate-100 text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700",
  };
}

export const FactorExposurePanel: React.FC<FactorExposurePanelProps> = ({ factorExposure }) => {
  const sysPct = Number((factorExposure.systematic_variance_share * 100).toFixed(1));
  const idioPct = Number((factorExposure.idiosyncratic_variance_share * 100).toFixed(1));

  const chartData = useMemo(
    () =>
      factorExposure.factors.map((f) => ({
        code: f.factor_code,
        name: f.factor_name,
        proxy: f.proxy_ticker,
        beta: Number(f.beta.toFixed(3)),
        variancePct: Number((f.variance_contribution_pct * 100).toFixed(1)),
        corr: Number(f.correlation.toFixed(2)),
        tStat: Number(f.t_stat.toFixed(2)),
      })),
    [factorExposure.factors]
  );

  const isMacroOls = factorExposure.methodology === "macro_etf_ols";

  return (
    <div className="space-y-6">
      {/* Top Banner: Systematic R² vs Idiosyncratic Risk Split */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <Compass className="h-5 w-5 text-indigo-500 dark:text-indigo-400" />
              <h3 className="text-base font-semibold text-slate-800 dark:text-white">
                Systematic Macro Factor Attribution vs. Idiosyncratic Risk
              </h3>
              <span
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${
                  isMacroOls
                    ? "bg-indigo-500/15 text-indigo-700 ring-indigo-500/30 dark:text-indigo-300"
                    : "bg-amber-500/15 text-amber-700 ring-amber-500/30 dark:text-amber-300"
                }`}
              >
                {isMacroOls ? "Multi-Factor Macro ETF OLS" : "Statistical Orthogonal PCA Fallback"}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Decomposes portfolio variance into systematic macro drivers (Equity Market, Tech Duration, Size, Sovereign Rates, Gold, Commodities) and unexplained security-specific risk.
            </p>
          </div>

          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Systematic Share (R²)
              </div>
              <div className="font-mono text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {sysPct.toFixed(1)}%
              </div>
            </div>
            <div className="h-8 w-px bg-slate-200 dark:bg-slate-800" />
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                Idiosyncratic (1 − R²)
              </div>
              <div className="font-mono text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {idioPct.toFixed(1)}%
              </div>
            </div>
          </div>
        </div>

        {/* Stacked Systematic vs Idiosyncratic Progress Bar */}
        <div className="mt-5">
          <div className="flex h-3 w-full overflow-hidden rounded-full bg-slate-100 p-0.5 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
            <div
              className="h-full rounded-l-full bg-indigo-500 transition-all duration-300"
              style={{ width: `${Math.min(100, Math.max(0, sysPct))}%` }}
              title={`Systematic Variance: ${sysPct}%`}
            />
            <div
              className="h-full rounded-r-full bg-emerald-500/80 transition-all duration-300"
              style={{ width: `${Math.min(100, Math.max(0, idioPct))}%` }}
              title={`Idiosyncratic Variance: ${idioPct}%`}
            />
          </div>
          <div className="mt-2 flex flex-wrap justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-indigo-500" />
              Systematic Macro Betas explain{" "}
              <strong className="font-mono text-slate-700 dark:text-slate-200">{sysPct}%</strong> of daily return variance
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Stock-specific / alpha dispersion accounts for{" "}
              <strong className="font-mono text-slate-700 dark:text-slate-200">{idioPct}%</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Middle Row: Factor Beta Sensitivities + Factor Variance Contribution Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Chart 1: Multi-Factor Beta Sensitivities */}
        <div className={cardClass}>
          <div>
            <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
              <Sliders className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
              Multi-Factor Beta Sensitivities (β)
            </h4>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Partial regression slope of portfolio returns per 1.00 unit move in each macro factor proxy.
            </p>
          </div>

          <div className="mt-4 h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 24, left: 16, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} strokeOpacity={0.4} />
                <XAxis type="number" stroke={CHART_AXIS} tick={{ fontSize: 11, fill: CHART_AXIS }} />
                <YAxis
                  type="category"
                  dataKey="code"
                  stroke={CHART_AXIS}
                  tick={{ fontSize: 11, fontWeight: 600, fill: CHART_AXIS }}
                  width={65}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: number) => [value.toFixed(3), "Factor Beta (β)"]}
                  labelFormatter={(label, payload) => {
                    const row = payload?.[0]?.payload as { name?: string; proxy?: string } | undefined;
                    return row ? `${row.name} (${row.proxy})` : label;
                  }}
                />
                <ReferenceLine x={0} stroke="#475569" strokeWidth={1.5} />
                <Bar dataKey="beta" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, idx) => (
                    <Cell key={`beta-cell-${idx}`} fill={entry.beta >= 0 ? "#6366f1" : "#10b981"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 2: Euler Factor Variance Attribution (%) */}
        <div className={cardClass}>
          <div>
            <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white">
              <Layers className="h-4 w-4 text-sky-500 dark:text-sky-400" />
              Factor Variance Contribution Share (%)
            </h4>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Percentage of total portfolio variance driven by each factor (sums to R² = {sysPct}%).
            </p>
          </div>

          <div className="mt-4 h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 24, left: 16, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} strokeOpacity={0.4} />
                <XAxis type="number" unit="%" domain={[0, 100]} stroke={CHART_AXIS} tick={{ fontSize: 11, fill: CHART_AXIS }} />
                <YAxis
                  type="category"
                  dataKey="code"
                  stroke={CHART_AXIS}
                  tick={{ fontSize: 11, fontWeight: 600, fill: CHART_AXIS }}
                  width={65}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value: number) => [`${value.toFixed(1)}%`, "Variance Explained"]}
                  labelFormatter={(label, payload) => {
                    const row = payload?.[0]?.payload as { name?: string; proxy?: string } | undefined;
                    return row ? `${row.name} (${row.proxy})` : label;
                  }}
                />
                <Bar dataKey="variancePct" fill="#38bdf8" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom Table: Detailed Factor Regression Diagnostics */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
        <div className="flex items-center justify-between border-b border-slate-200 pb-4 dark:border-slate-800">
          <div>
            <h4 className="flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-white">
              <Cpu className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
              Econometric Factor Regression Diagnostics
            </h4>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Ridge-stabilized multi-factor OLS sensitivities, t-statistics, standalone factor volatilities, and variance shares.
            </p>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-slate-200 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:border-slate-800 dark:bg-slate-950/70 dark:text-slate-400">
              <tr>
                <th className="px-3 py-3">Factor Code</th>
                <th className="px-3 py-3">Macro / Style Dimension</th>
                <th className="px-3 py-3">Proxy Benchmark</th>
                <th className="px-3 py-3">OLS Beta (β)</th>
                <th className="px-3 py-3">t-Statistic &amp; Significance</th>
                <th className="px-3 py-3">Standalone Corr</th>
                <th className="px-3 py-3">Factor Ann. Vol</th>
                <th className="px-3 py-3">Variance Share</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200/70 dark:divide-slate-800/60">
              {factorExposure.factors.map((fac) => {
                const sig = getSignificanceBadge(fac.t_stat);
                const varPct = fac.variance_contribution_pct * 100;
                return (
                  <tr key={fac.factor_code} className="transition hover:bg-slate-100/60 dark:hover:bg-slate-800/40">
                    <td className="px-3 py-3 font-mono font-bold text-indigo-600 dark:text-indigo-400">
                      {fac.factor_code}
                    </td>
                    <td className="px-3 py-3 font-medium text-slate-800 dark:text-white">{fac.factor_name}</td>
                    <td className="px-3 py-3">
                      <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {fac.proxy_ticker}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`font-mono font-semibold ${
                          fac.beta > 0.35
                            ? "text-amber-600 dark:text-amber-300"
                            : fac.beta < -0.1
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-slate-900 dark:text-white"
                        }`}
                      >
                        {fac.beta >= 0 ? "+" : ""}
                        {fac.beta.toFixed(3)}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-12 font-mono text-slate-600 dark:text-slate-300">
                          {fac.t_stat >= 0 ? "+" : ""}
                          {fac.t_stat.toFixed(2)}
                        </span>
                        <span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${sig.classes}`}>
                          {sig.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3 font-mono text-slate-600 dark:text-slate-300">
                      {fac.correlation >= 0 ? "+" : ""}
                      {fac.correlation.toFixed(2)}
                    </td>
                    <td className="px-3 py-3 font-mono text-slate-600 dark:text-slate-300">
                      {(fac.annualized_factor_vol * 100).toFixed(2)}%
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="h-2 w-20 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                          <div
                            className="h-full rounded-full bg-sky-400"
                            style={{ width: `${Math.min(100, Math.max(0, varPct))}%` }}
                          />
                        </div>
                        <span className="font-mono font-semibold text-slate-900 dark:text-white">
                          {varPct.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
